import React, { useEffect, useState } from 'react';
import { Description } from '@mui/icons-material';
import { Colors } from 'library';
import { EmptyText, Link as UiLink, Row, Stack } from 'library/ui';
import download from 'shared/Download';
import { client, track } from 'shared';
import AddressSelectModal from './AddressSelectModal';
import { useUserContext } from 'UserContext';
import { DocumentLink } from 'internal/admin-documents/types';

async function downloadFile(path: string) {
    try {
        await download(path);
    } catch (error) {
        track('Document Download Error:', error);
    }
}

export const getDocumentLinksForUserId = async (
    userId: number,
    for_folder: boolean = false,
    documents_already_listed: boolean = false,
): Promise<DocumentLink[]> => {
    let url = `api/documents/document-links/?user_id=${userId}`;
    if (for_folder) {
        url += '&for_folder=true';
    } else if (documents_already_listed) {
        url += '&documents_already_listed=true';
    }
    const links = await client(url);
    return links;
};

export const getDocumentsFolderForUserId = (
    userId: number,
    showModal?: () => void,
    setLoadingFalse?: () => void,
    businessLocationId?: number,
    programId?: string,
) => {
    let url = `api/documents/zip-documents/?user_id=${userId}`;
    if (businessLocationId) {
        url += `&business_location_id=${businessLocationId}`;
    }
    if (programId) {
        url += `&program_id=${programId}`;
    }

    downloadFile(url)
        .then(() => {
            showModal?.();
        })
        .then(() => {
            getDocumentLinksForUserId(userId, true).then((links) => {
                links.forEach((link) => {
                    window.open(link.url, '_blank');
                });
            });
        })
        .finally(() => {
            setLoadingFalse?.();
        });
};

export const Link = ({ item }: { item: DocumentLink }) => {
    const [showModal, setShowModal] = useState(false);
    const [downLoading, setDownloading] = useState(false);

    const { user } = useUserContext();
    const userAddresses = user?.address;
    if (item.type === 'download' || item.type === 'prompt') {
        const onClick = () => {
            setDownloading(true);
            downloadFile(item.url)
                .then(() => setDownloading(false))
                .catch(() => setDownloading(false));
        };
        const onPromptClick = () => {
            if (userAddresses?.length === 1) {
                onModalSelect(userAddresses[0].id);
            } else {
                setShowModal(true);
            }
        };

        const onModalSelect = (addressId: string | number) => {
            setDownloading(true);
            downloadFile(`${item.url}&address_id=${addressId}`)
                .then(() => {
                    setDownloading(false);
                    setShowModal(false);
                })
                .catch(() => setDownloading(false));
        };

        const clickEvent = item.type === 'prompt' ? onPromptClick : onClick;
        return (
            <span>
                <UiLink weight="medium" onClick={downLoading ? () => {} : clickEvent}>
                    {item.name}
                </UiLink>
                <AddressSelectModal
                    isOpen={showModal}
                    onClose={() => setShowModal(false)}
                    onAddressSelect={onModalSelect}
                />
            </span>
        );
    }

    return (
        <UiLink weight="medium" href={item.url}>
            {item.name}
        </UiLink>
    );
};

export default function DocumentLinks({ userId }: { userId: number }) {
    const [links, setLinks] = useState<DocumentLink[]>([]);

    useEffect(() => {
        getDocumentLinksForUserId(userId, false, false).then(setLinks);
    }, [userId]);

    if (!links.length) {
        return <EmptyText>There are no documents to show for this provider.</EmptyText>;
    }

    return (
        <Stack gap="sm">
            {links.map((x) => (
                <Row key={x.url} gap="sm" align="center">
                    <Description sx={{ fontSize: 18, color: Colors.textSecondary }} />
                    <Link item={x} />
                </Row>
            ))}
        </Stack>
    );
}
